import { useCallback, useEffect, useState } from "react";
import { fetchJson } from "../lib/fetchJson.ts";
import { Refusal, when } from "./bits.tsx";

// The Credentials screen: every secret the platform owns, by fingerprint only.
// Model credentials sit one per provider behind a one-time terms
// acknowledgement; platform secrets (the GitHub App's three, the Resend key,
// the Daytona API key) are the same write-only put, and no route ever returns
// a value. OpenRouter also holds a management key, which the Spend screen's
// bill check reads the bill with. See ADR-0039, ADR-0040 and ADR-0051 §5.

/** The record that an operator acknowledged a provider's terms, and when. */
export type TermsAcknowledgement = {
  provider: string;
  acknowledgedBy: string;
  acknowledgedAt: string;
};

/** A stored secret as the screen sees it: its fingerprint and who set it when, never the value. */
export type Stored = { fingerprint: string; addedBy: string; addedAt: string };

/**
 * One model provider: its terms text, the acknowledgement if any, and the
 * stored credential if any. `managementKey` is present on OpenRouter only:
 * the key the bill check reads the provider's bill with.
 */
export type ProviderCredentialView = {
  provider: string;
  terms: string;
  acknowledged: TermsAcknowledgement | null;
  credential: Stored | null;
  managementKey?: Stored | null;
};

/** One platform secret slot, grouped by the panel it belongs to, with its stored value if any. */
export type PlatformSecretView = {
  id: string;
  group: "github-app" | "mail" | "sandbox" | "streaming";
  label: string;
  secret: Stored | null;
};

/** What `GET /api/credentials` returns: every provider and every platform secret slot. */
export type CredentialsView = {
  providers: ProviderCredentialView[];
  secrets: PlatformSecretView[];
};

function storedLine(s: Stored): string {
  return `${s.fingerprint} · set ${when(s.addedAt)} by ${s.addedBy}`;
}

/**
 * The Credentials screen: model credentials per provider, the GitHub App
 * secrets, the mail key, the sandbox key and the streaming key, each as a fingerprint with an
 * add-or-replace form. Reads `/api/credentials` once on mount and takes the
 * fresh view each write returns. Rendered by the admin shell at `/credentials`.
 */
export function CredentialsPage(): JSX.Element {
  const [view, setView] = useState<CredentialsView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await fetchJson<{ credentials: CredentialsView }>("/api/credentials");
      setView(body.credentials);
      setLoadError(null);
    } catch (e) {
      setLoadError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const github = view?.secrets.filter((s) => s.group === "github-app") ?? [];
  const mail = view?.secrets.filter((s) => s.group === "mail") ?? [];
  const sandbox = view?.secrets.filter((s) => s.group === "sandbox") ?? [];
  const streaming = view?.secrets.filter((s) => s.group === "streaming") ?? [];

  return (
    <>
      <p className="lede">
        Every secret the platform holds: the model credentials every customer&apos;s Runs are
        made with, the GitHub App&apos;s three secrets, the Resend key and the Daytona API key.
        Each is write-only — what you see is a fingerprint, and a value put here is used from
        the next Run or the next mail with no restart. A provider&apos;s terms are acknowledged once, before its
        first key.
      </p>

      <Refusal message={loadError} />

      <section className="panel" aria-label="model credentials">
        <div className="panel-head">
          <h2>Model credentials</h2>
          {view ? (
            <span className="meta" data-testid="credentials-count">
              {view.providers.filter((p) => p.credential).length} of {view.providers.length} providers
            </span>
          ) : null}
        </div>
        <div className="panel-body flush scroll-x">
          {view === null ? (
            <p aria-live="polite">Loading…</p>
          ) : (
            <table className="grid">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Terms</th>
                  <th>Credential</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {view.providers.map((p) => (
                  <ProviderRow key={p.provider} p={p} onChanged={setView} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="panel" aria-label="GitHub App">
        <div className="panel-head">
          <h2>GitHub App</h2>
          {view ? (
            <span className={github.every((s) => s.secret) ? "pill ok" : "pill warn"} data-testid="github-app-secrets">
              {github.filter((s) => s.secret).length} of {github.length} secrets
            </span>
          ) : null}
        </div>
        <div className="panel-body">
          {view === null ? (
            <p aria-live="polite">Loading…</p>
          ) : (
            github.map((s) => <SecretField key={s.id} s={s} multiline={s.id === "github-app-private-key"} onChanged={setView} />)
          )}
        </div>
      </section>

      <section className="panel" aria-label="mail">
        <div className="panel-head">
          <h2>Mail</h2>
        </div>
        <div className="panel-body">
          {view === null ? (
            <p aria-live="polite">Loading…</p>
          ) : (
            mail.map((s) => <SecretField key={s.id} s={s} multiline={false} onChanged={setView} />)
          )}
        </div>
      </section>

      <section className="panel" aria-label="sandbox">
        <div className="panel-head">
          <h2>Sandbox</h2>
          <span className="meta">
            Read when a Run opens a sandbox on the Daytona backend; a Run on that backend is
            refused at dispatch while it is not set.
          </span>
        </div>
        <div className="panel-body">
          {view === null ? (
            <p aria-live="polite">Loading…</p>
          ) : (
            sandbox.map((s) => <SecretField key={s.id} s={s} multiline={false} onChanged={setView} />)
          )}
        </div>
      </section>

      <section className="panel" aria-label="streaming">
        <div className="panel-head">
          <h2>Streamed devices</h2>
          <span className="meta">
            Read by the control plane when it uploads an Android build to Appetize or starts a streamed
            device; it never enters a sandbox. While it is not set, the build rows read &quot;stream
            unavailable&quot; and the downloads still work.
          </span>
        </div>
        <div className="panel-body">
          {view === null ? (
            <p aria-live="polite">Loading…</p>
          ) : (
            streaming.map((s) => <SecretField key={s.id} s={s} multiline={false} onChanged={setView} />)
          )}
        </div>
      </section>
    </>
  );
}

// One provider: its terms and their acknowledgement, its fingerprint, and
// the add-or-replace form. The form opens only once the terms are
// acknowledged; the acknowledgement shows the terms and asks once.
function ProviderRow({
  p,
  onChanged,
}: {
  p: ProviderCredentialView;
  onChanged: (v: CredentialsView) => void;
}): JSX.Element {
  const [reading, setReading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function acknowledge(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<{ credentials: CredentialsView }>(
        `/api/credentials/providers/${encodeURIComponent(p.provider)}/terms`,
        { method: "POST" },
      );
      setReading(false);
      onChanged(body.credentials);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function put(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<{ credentials: CredentialsView }>(
        `/api/credentials/providers/${encodeURIComponent(p.provider)}`,
        {
          method: "PUT",
          body: JSON.stringify(baseUrl.trim() === "" ? { apiKey } : { apiKey, baseUrl: baseUrl.trim() }),
        },
      );
      setApiKey("");
      setBaseUrl("");
      setEditing(false);
      onChanged(body.credentials);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const keyId = `credential-${p.provider}-key`;
  const urlId = `credential-${p.provider}-base-url`;

  return (
    <>
      <tr data-testid={`provider-${p.provider}`}>
        <td className="mono">{p.provider}</td>
        <td>
          {p.acknowledged ? (
            <span className="tiny">
              acknowledged {when(p.acknowledged.acknowledgedAt)} by {p.acknowledged.acknowledgedBy}
            </span>
          ) : (
            <span className="pill warn">not acknowledged</span>
          )}
        </td>
        <td>
          {p.credential ? (
            <span className="tiny" data-testid={`credential-${p.provider}`}>{storedLine(p.credential)}</span>
          ) : (
            <span className="dim">none</span>
          )}
        </td>
        <td>
          {p.acknowledged ? (
            editing ? null : (
              <button type="button" className="btn ghost sm" onClick={() => setEditing(true)}>
                {p.credential ? "Replace" : "Add"}
              </button>
            )
          ) : reading ? null : (
            <button type="button" className="btn ghost sm" onClick={() => setReading(true)}>
              Read terms
            </button>
          )}
        </td>
      </tr>
      {reading ? (
        <tr>
          <td colSpan={4}>
            <div className="callout" role="note" aria-label={`${p.provider} terms`}>
              <p>{p.terms}</p>
              <span className="btn-row">
                <button type="button" className="btn primary sm" disabled={busy} onClick={() => void acknowledge()}>
                  {busy ? "Acknowledging…" : "Acknowledge"}
                </button>
                <button type="button" className="btn ghost sm" disabled={busy} onClick={() => setReading(false)}>
                  Cancel
                </button>
              </span>
              <Refusal message={error} />
            </div>
          </td>
        </tr>
      ) : null}
      {p.managementKey !== undefined ? (
        <tr>
          <td colSpan={4}>
            <ManagementKeyField provider={p.provider} stored={p.managementKey} onChanged={onChanged} />
          </td>
        </tr>
      ) : null}
      {editing ? (
        <tr>
          <td colSpan={4}>
            <form
              aria-label={`${p.provider} credential`}
              onSubmit={(e) => {
                e.preventDefault();
                void put();
              }}
            >
              <div className="field">
                <label htmlFor={keyId}>API key</label>
                <input
                  id={keyId}
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  disabled={busy}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor={urlId}>Base URL (optional)</label>
                <input
                  id={urlId}
                  type="url"
                  value={baseUrl}
                  disabled={busy}
                  onChange={(e) => setBaseUrl(e.target.value)}
                />
              </div>
              <span className="btn-row">
                <button type="submit" className="btn primary sm" disabled={busy || apiKey.trim() === ""}>
                  {busy ? "Saving…" : p.credential ? "Replace credential" : "Add credential"}
                </button>
                <button type="button" className="btn ghost sm" disabled={busy} onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </span>
              <Refusal message={error} />
            </form>
          </td>
        </tr>
      ) : null}
    </>
  );
}

// A provider's management key: the fingerprint it has and a write-only field
// to add or replace it. Replacing the model credential leaves it alone.
function ManagementKeyField({
  provider,
  stored,
  onChanged,
}: {
  provider: string;
  stored: Stored | null;
  onChanged: (v: CredentialsView) => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = `management-key-${provider}-input`;

  async function put(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<{ credentials: CredentialsView }>(
        `/api/credentials/providers/${encodeURIComponent(provider)}/management-key`,
        { method: "PUT", body: JSON.stringify({ managementKey: value }) },
      );
      setValue("");
      onChanged(body.credentials);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="field"
      aria-label={`${provider} management key`}
      onSubmit={(e) => {
        e.preventDefault();
        void put();
      }}
    >
      <label htmlFor={id}>Management key (optional), read by the Spend screen&apos;s bill check</label>
      <p className="tiny" data-testid={`management-key-${provider}`}>
        {stored ? storedLine(stored) : "not set"}
      </p>
      <input
        id={id}
        type="password"
        autoComplete="off"
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
      />
      <span className="btn-row">
        <button type="submit" className="btn primary sm" disabled={busy || value.trim() === ""}>
          {busy ? "Saving…" : stored ? "Replace" : "Set"}
        </button>
      </span>
      <Refusal message={error} />
    </form>
  );
}

// One platform secret: the fingerprint it has, and a write-only field to
// put a new value. The private key is a PEM block, so its field is a
// textarea; the others are one line.
function SecretField({
  s,
  multiline,
  onChanged,
}: {
  s: PlatformSecretView;
  multiline: boolean;
  onChanged: (v: CredentialsView) => void;
}): JSX.Element {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = `secret-${s.id}`;

  async function put(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const body = await fetchJson<{ credentials: CredentialsView }>(
        `/api/credentials/secrets/${encodeURIComponent(s.id)}`,
        { method: "PUT", body: JSON.stringify({ value }) },
      );
      setValue("");
      onChanged(body.credentials);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="field"
      aria-label={s.label}
      onSubmit={(e) => {
        e.preventDefault();
        void put();
      }}
    >
      <label htmlFor={id}>{s.label}</label>
      <p className="tiny" data-testid={`secret-${s.id}`}>
        {s.secret ? storedLine(s.secret) : "not set"}
      </p>
      {multiline ? (
        <textarea id={id} rows={4} value={value} disabled={busy} onChange={(e) => setValue(e.target.value)} />
      ) : (
        <input
          id={id}
          type="password"
          autoComplete="off"
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
        />
      )}
      <span className="btn-row">
        <button type="submit" className="btn primary sm" disabled={busy || value.trim() === ""}>
          {busy ? "Saving…" : s.secret ? "Replace" : "Set"}
        </button>
      </span>
      <Refusal message={error} />
    </form>
  );
}
